import type { LocalizationStaging } from "src/cep/messages/message/message-composer/composer-surface";
import { NATIVE_DELIVERY_PERMISSIONS } from "src/cep/permissions";
import type { ResourcePermissionInput } from "src/components/permission/use-resource-permission";
import {
  type CepLocalizationOwnerInput,
  CepLocalizationOwnerType,
  type CepLocalizationSettingsBoolExp,
  type CepTranslationsBoolExp,
} from "src/graphql/types";

import { ANY_LOCALE_RULES, type LocaleRules } from "./locale-rules";
import { WHATSAPP_LOCALE_RULES } from "./whatsapp-locales";

export type LocalizationOwner =
  | { type: "message"; id: string }
  | { type: "contentBlock"; id: string }
  | { type: "footer"; id: string }
  | { type: "emailTemplate"; id: string }
  | { type: "whatsappTemplate"; id: string }
  | { type: "whatsappLibraryTemplate"; id: string };

export type LocalizationOwnerType = LocalizationOwner["type"];

export function messageLocalizationOwner(
  messageId: string | null,
): LocalizationOwner | null {
  return messageId === null ? null : { type: "message", id: messageId };
}

export function emailTemplateLocalizationOwner(
  templateId: string | null,
): LocalizationOwner | null {
  return templateId === null ? null : { type: "emailTemplate", id: templateId };
}

/** A WhatsApp message's translations belong to its own template, which holds a version per language. */
export function whatsAppTemplateLocalizationOwner(
  templateId: string | null,
): LocalizationOwner | null {
  return templateId === null
    ? null
    : { type: "whatsappTemplate", id: templateId };
}

/** A library WhatsApp template is shared by the messages that send it, so it is a library asset. */
export function whatsAppLibraryTemplateLocalizationOwner(
  templateId: string,
): LocalizationOwner {
  return { type: "whatsappLibraryTemplate", id: templateId };
}

type WhatsAppTemplateOwner = Extract<
  LocalizationOwner,
  { type: "whatsappTemplate" | "whatsappLibraryTemplate" }
>;

/** Both WhatsApp owners localize a template, which holds a version per language. */
export function isWhatsAppTemplateOwner(
  owner: LocalizationOwner | null,
): owner is WhatsAppTemplateOwner {
  return (
    owner !== null &&
    ownerEntry<OwnerPolicyEntry>(OWNER_POLICY, owner.type).storage ===
      "whatsappTemplate"
  );
}

/** Only a message carries A/B arms. */
export function ownerMessageId(owner: LocalizationOwner | null): string | null {
  return owner?.type === "message" ? owner.id : null;
}

/**
 * Only a message host can be missing a row: a journey node holds one that the
 * journey save has yet to create, and a content block always has its own.
 */
export function ownerTypeOf(
  owner: LocalizationOwner | null,
): LocalizationOwnerType {
  return owner?.type ?? "message";
}

export type LocalizationOwnerNoun =
  | "message"
  | "content block"
  | "footer"
  | "template";

type OwnerStorage =
  | "message"
  | "contentBlock"
  | "emailTemplate"
  | "whatsappTemplate";

export type LocalizationOwnerPolicy = {
  /**
   * Drafting a message and managing the shared asset library are separate
   * grants, and Hasura's row permissions on `cep_localization_settings`
   * enforce the same split.
   */
  permission: ResourcePermissionInput<"workspace">;
  noun: LocalizationOwnerNoun;
  /**
   * What the host calls the action that commits pending edits, so copy asking
   * for one names the button the marketer is looking at.
   */
  saveVerb: "Save" | "Publish";
  /**
   * Whether settings changes may buffer on a host's staging buffer instead of
   * reaching Hasura. A host opts in by providing the buffer; a campaign never does.
   */
  acceptsStaging: boolean;
  /**
   * Disabling unmounts the Localize table, which drops drafts the host has not
   * staged yet. A block host publishes them together, so it refuses the disable.
   */
  blocksDisableWhileDrafting: boolean;
  /**
   * A subject ships with the email, so translating a body under a
   * source-language one is a half-localized send. A content block has no
   * subject of its own, and a template never sends: the message it is applied
   * to carries that requirement.
   */
  requiresSubject: boolean;
  /**
   * Whether a handful of elements flips the translation table onto locale rows.
   * A block is a few elements against many locales; a message is the reverse.
   */
  transposesFewElements: boolean;
  /**
   * Whether the preview draws the email header rows. A content block has
   * neither subject nor preheader; a template carries both.
   */
  hasMetadata: boolean;
  /** Which locales the settings dialog offers as the default and as targets. */
  localeRules: LocaleRules;
  /**
   * Whether locales can be added or removed while the campaign is live
   */
  editsLocalesWhileLive: boolean;
};

type OwnerPolicyEntry = LocalizationOwnerPolicy & {
  /** Which table's id addresses the owner's settings and translation rows. */
  storage: OwnerStorage;
};

// A footer is a content block with a different grant and different copy, so
// every rule about the content itself matches the content block's.
const OWNER_POLICY = {
  message: {
    storage: "message",
    permission: NATIVE_DELIVERY_PERMISSIONS.draft,
    noun: "message",
    saveVerb: "Save",
    acceptsStaging: true,
    blocksDisableWhileDrafting: false,
    requiresSubject: true,
    transposesFewElements: false,
    hasMetadata: true,
    localeRules: ANY_LOCALE_RULES,
    editsLocalesWhileLive: false,
  },
  contentBlock: {
    storage: "contentBlock",
    permission: NATIVE_DELIVERY_PERMISSIONS.manageAssets,
    noun: "content block",
    saveVerb: "Publish",
    acceptsStaging: true,
    blocksDisableWhileDrafting: true,
    requiresSubject: false,
    transposesFewElements: true,
    hasMetadata: false,
    localeRules: ANY_LOCALE_RULES,
    editsLocalesWhileLive: false,
  },
  footer: {
    storage: "contentBlock",
    permission: NATIVE_DELIVERY_PERMISSIONS.manageSettings,
    noun: "footer",
    saveVerb: "Publish",
    acceptsStaging: true,
    blocksDisableWhileDrafting: true,
    requiresSubject: false,
    transposesFewElements: true,
    hasMetadata: false,
    localeRules: ANY_LOCALE_RULES,
    editsLocalesWhileLive: false,
  },
  emailTemplate: {
    storage: "emailTemplate",
    permission: NATIVE_DELIVERY_PERMISSIONS.manageAssets,
    noun: "template",
    saveVerb: "Publish",
    acceptsStaging: false,
    blocksDisableWhileDrafting: false,
    requiresSubject: false,
    transposesFewElements: false,
    hasMetadata: true,
    localeRules: ANY_LOCALE_RULES,
    editsLocalesWhileLive: false,
  },
  // A message's own WhatsApp template is the message's content, so it reads and
  // is gated like the message.
  whatsappTemplate: {
    storage: "whatsappTemplate",
    permission: NATIVE_DELIVERY_PERMISSIONS.draft,
    noun: "message",
    saveVerb: "Save",
    acceptsStaging: false,
    blocksDisableWhileDrafting: false,
    requiresSubject: false,
    transposesFewElements: false,
    hasMetadata: false,
    localeRules: WHATSAPP_LOCALE_RULES,
    editsLocalesWhileLive: true,
  },
  // The same storage as a message's own template, gated and worded as the shared asset it is.
  whatsappLibraryTemplate: {
    storage: "whatsappTemplate",
    permission: NATIVE_DELIVERY_PERMISSIONS.manageAssets,
    noun: "template",
    saveVerb: "Save",
    acceptsStaging: false,
    blocksDisableWhileDrafting: false,
    requiresSubject: false,
    transposesFewElements: false,
    hasMetadata: false,
    localeRules: WHATSAPP_LOCALE_RULES,
    editsLocalesWhileLive: true,
  },
} as const satisfies Record<LocalizationOwnerType, OwnerPolicyEntry>;

/** Throws on a type outside the union, which must fail loudly rather than take a message's path. */
export function ownerEntry<Entry>(
  entries: Readonly<Record<LocalizationOwnerType, Entry>>,
  type: LocalizationOwnerType,
): Entry {
  if (!Object.hasOwn(entries, type)) {
    throw new Error(`Unsupported localization owner type: ${String(type)}`);
  }
  return entries[type];
}

export function ownerPolicy(
  type: LocalizationOwnerType,
): LocalizationOwnerPolicy {
  return ownerEntry<LocalizationOwnerPolicy>(OWNER_POLICY, type);
}

/** The host's staging buffer, for an owner whose changes may wait on the host's save; the others write directly. */
export function ownerStaging(
  owner: LocalizationOwner | null,
  staging: LocalizationStaging | undefined,
): LocalizationStaging | undefined {
  return ownerPolicy(ownerTypeOf(owner)).acceptsStaging ? staging : undefined;
}

type OwnerIdColumns = {
  messageId: string | null;
  contentBlockId: string | null;
  emailTemplateId: string | null;
};

type OwnerRowWhere =
  | { message_id: { _eq: string } }
  | { content_block_id: { _eq: string } }
  | { email_template_id: { _eq: string } }
  | { cep_whatsapp_template_id: { _eq: string } };

const NO_OWNER_COLUMNS: OwnerIdColumns = {
  messageId: null,
  contentBlockId: null,
  emailTemplateId: null,
};

const OWNER_STORAGE = {
  message: {
    inputType: CepLocalizationOwnerType.Message,
    rowWhere: (id) => ({ message_id: { _eq: id } }),
    idColumns: (id) => ({ ...NO_OWNER_COLUMNS, messageId: id }),
  },
  contentBlock: {
    inputType: CepLocalizationOwnerType.ContentBlock,
    rowWhere: (id) => ({ content_block_id: { _eq: id } }),
    idColumns: (id) => ({ ...NO_OWNER_COLUMNS, contentBlockId: id }),
  },
  emailTemplate: {
    inputType: CepLocalizationOwnerType.EmailTemplate,
    rowWhere: (id) => ({ email_template_id: { _eq: id } }),
    idColumns: (id) => ({ ...NO_OWNER_COLUMNS, emailTemplateId: id }),
  },
  // Its settings are written by `setCepWhatsAppTemplateLocalization`, which also
  // creates a version per language, so there are no Hasura insert columns.
  whatsappTemplate: {
    inputType: CepLocalizationOwnerType.WhatsappTemplate,
    rowWhere: (id) => ({ cep_whatsapp_template_id: { _eq: id } }),
    idColumns: null,
  },
} satisfies Record<
  OwnerStorage,
  {
    inputType: CepLocalizationOwnerType;
    rowWhere: (id: string) => OwnerRowWhere;
    idColumns: ((id: string) => OwnerIdColumns) | null;
  }
>;

function ownerStorage(owner: LocalizationOwner) {
  return OWNER_STORAGE[
    ownerEntry<OwnerPolicyEntry>(OWNER_POLICY, owner.type).storage
  ];
}

/** Explicit refetches can run disabled queries, so a missing owner must match no rows. */
const NO_OWNER = { id: { _is_null: true } } as const;

/** Hasura treats an empty Boolean expression as unrestricted. */
function ownerRowWhere(
  owner: LocalizationOwner | null,
): OwnerRowWhere | typeof NO_OWNER {
  return owner === null ? NO_OWNER : ownerStorage(owner).rowWhere(owner.id);
}

export function ownerSettingsWhere(
  owner: LocalizationOwner | null,
): CepLocalizationSettingsBoolExp {
  return ownerRowWhere(owner);
}

export function ownerTranslationsWhere(
  owner: LocalizationOwner | null,
): CepTranslationsBoolExp {
  return ownerRowWhere(owner);
}

/**
 * `applyCepTranslationEdits` still accepts the pre-owner `messageId` and
 * `contentBlockId` arguments, but it rejects a call that supplies both forms,
 * and there is no legacy argument for a template. One typed owner keeps every
 * call on the arm the resolver switches on.
 */
export function ownerEditInput(
  owner: LocalizationOwner,
): CepLocalizationOwnerInput {
  return { id: owner.id, type: ownerStorage(owner).inputType };
}

export function ownerInsertVariables(owner: LocalizationOwner): OwnerIdColumns {
  const { idColumns } = ownerStorage(owner);
  if (idColumns === null) {
    throw new Error(
      `Localization settings for a ${owner.type} are not written through Hasura`,
    );
  }
  return idColumns(owner.id);
}
